import * as _ from "lodash"
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import axios from "axios";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { esWB_Company, esWB_SalesOrg } from "../PA.Core/PAdbSchema";
import { WB_CompanyRow, WB_SalesOrgRow } from "../PA.Core/PAdbSchemaRows";


export interface IModelDataSetServiceRequest {
    RequestType?: string;
    GUID?: string;
    wbMDSetId: number;
    wbSalesOrgId: number,
    wbModelId: number,
    effectiveDate?: string,
    wbEntityIdCustomer: number,
    wbEntityIdProduct: number,
    wbEntityIdPeriod: number,
    wbCustHId: number,
    wbPrdHId: number,
    wbPeriodHId: number,
    wbCustIds: number[],
    wbPrdGrpIds: number[],
    wbPeriodIds: number[]
}

export function isAnIModelDataSetServiceRequest(obj: unknown): obj is IModelDataSetServiceRequest {
    const cast = obj as IModelDataSetServiceRequest

    switch (true) {
        case cast.wbMDSetId === undefined || typeof cast.wbMDSetId !== 'number':
        case cast.wbSalesOrgId === undefined || typeof cast.wbSalesOrgId !== 'number':
        case cast.wbModelId === undefined || typeof cast.wbModelId !== 'number':
        case cast.wbEntityIdCustomer === undefined || typeof cast.wbEntityIdCustomer !== 'number':
        case cast.wbEntityIdProduct === undefined || typeof cast.wbEntityIdProduct !== 'number':
        case cast.wbEntityIdPeriod === undefined || typeof cast.wbEntityIdPeriod !== 'number':
        case cast.wbCustHId === undefined || typeof cast.wbCustHId !== 'number':
        case cast.wbPrdHId === undefined || typeof cast.wbPrdHId !== 'number':
        case cast.wbPeriodHId === undefined || typeof cast.wbPeriodHId !== 'number':
        case !Array.isArray(cast.wbCustIds):
        case !Array.isArray(cast.wbPrdGrpIds):
        case !Array.isArray(cast.wbPeriodIds):
            return false
    }

    return true
}

export interface IModelDataSetServiceResponse {
    success: boolean,
    errors: string[],
    warnings: string[],
    infoMessages: string[],
    rows: IPredictorDataRow[]
}

function isPredictorDataRow(obj: unknown): obj is IPredictorDataRow {
    const cast = obj as IPredictorDataRow
    return (
        typeof cast.wbPrdId === 'number' &&
        typeof cast.wbCustId === 'number' &&
        typeof cast.wbPeriodId === 'number' &&
        typeof cast.predictors === 'object'
    )
}

export function isAnIModelDataSetServiceResponse(obj: unknown): obj is IModelDataSetServiceResponse {
    const cast = obj as IModelDataSetServiceResponse
    return (
        typeof cast.success === 'boolean' &&
        Array.isArray(cast.rows) &&
        cast.rows.every((item) => isPredictorDataRow(item)) &&
        Array.isArray(cast.errors) &&
        cast.errors.every((item) => typeof item === 'string') &&
        Array.isArray(cast.warnings) &&
        cast.warnings.every((item) => typeof item === 'string') &&
        Array.isArray(cast.infoMessages) &&
        cast.infoMessages.every((item) => typeof item === 'string')
    )
}

export interface IPredictorDataRow {
    wbCustId: number
    wbPrdId: number
    wbPeriodId: number
    predictors: IPredictors
}

export interface IPredictors {
    [PredictorId: string]: number
}

/** This const is used for logging in the ModelDataSetService on the server to let us know
 * when the type we receive doesn't match what we expect */
export const expectedDatasetResponseFormat = `
{
  success: boolean;
  errors: string[];
  warnings: string[];
  infoMessages: string[];
  rows: Array<{
    wbCustId: number;
    wbPrdId: number;
    wbPeriodId: number;
    predictors: {
      [PredictorId: string]: number;
    };
  }>;
}
`

export interface IPredictorDictionary {
    [wbCustId: number]: {
        [wbPrdId: number]: {
            [wbPeriodId: number]: IPredictors
        }
    }
}

export class ModelDataSetService {
    private static dbContext: PAdbContext

    static async ModelDataSet_getCPG(
        wbSalesOrgId: number,
        wbModelId: number,
        wbEntityIdCustomer: number,
        wbEntityIdProduct: number,
        wbEntityIdPeriod: number,
        wbCustHId: number,
        wbPrdHId: number,
        wbPeriodHId: number,
        wbCustIds: number[],
        wbPrdGrpIds: number[],
        wbPeriodIds: number[]
    ): Promise<IPredictorDictionary> {
        if (_.isEmpty(ModelDataSetService.dbContext)) ModelDataSetService.dbContext = new PAdbContext()

        const salesOrgRow = <WB_SalesOrgRow>(
            _.first(await ModelDataSetService.dbContext.findUsingCache(esWB_SalesOrg, [wbSalesOrgId]))
        )
        const wbCompanyId = salesOrgRow?.cif?.WB_COMPANY_ID || 0
        const companyRow = <WB_CompanyRow>(
            _.first(await ModelDataSetService.dbContext.findUsingCache(esWB_Company, [wbCompanyId]))
        )
        const wbMDSetId = companyRow?.cif?.WB_MDSET_ID || 0

        const req: IModelDataSetServiceRequest = {
            RequestType: 'ModelDataSet_getCPG',
            wbMDSetId: wbMDSetId,
            wbSalesOrgId: wbSalesOrgId,
            wbModelId: wbModelId,
            wbEntityIdCustomer: wbEntityIdCustomer,
            wbEntityIdProduct: wbEntityIdProduct,
            wbEntityIdPeriod: wbEntityIdPeriod,
            wbCustHId: wbCustHId,
            wbPrdHId: wbPrdHId,
            wbPeriodHId: wbPeriodHId,
            wbCustIds: wbCustIds,
            wbPrdGrpIds: wbPrdGrpIds,
            wbPeriodIds: wbPeriodIds
        }

        let resp: IModelDataSetServiceResponse
        try {
            resp = await ModelDataSetService.makeRequest(req)
            resp.errors.forEach((s) => console.error(s))
            resp.warnings.forEach((s) => console.warn(s))
            return this.createDictionary(resp)
        } catch (e: unknown) {
            let msg: string
            if (e instanceof Error) {
                msg = `Unable to complete ModelDataSetService request. Message: ${e.message}. Request: ${JSON.stringify(req)}`
            } else {
                msg = `Unknown exception when executing ModelDataSetService request. Contents: ${JSON.stringify(e)}. Request: ${JSON.stringify(req)}`
            }
            console.error(msg)
            return {}
        }
    }

    private static createDictionary(resp: IModelDataSetServiceResponse): IPredictorDictionary {
        const rv: IPredictorDictionary = {}
        resp.rows.forEach((item) => {
            if (!(item.wbCustId in rv)) {
                rv[item.wbCustId] = {}
            }
            const byPrd = rv[item.wbCustId]

            if (!(item.wbPrdId in byPrd)) {
                byPrd[item.wbPrdId] = {}
            }
            const byPeriod = byPrd[item.wbPrdId]

            // overwrite but log as a warning
            if (item.wbPeriodId in byPeriod) {
                console.warn(
                    `ModelDataSetService: multiple predictions found for combination: wbCustId=${item.wbCustId} wbPrdId=${item.wbPrdId} wbPeriodId=${item.wbPeriodId}. Overwriting with later values`
                )
            }
            byPeriod[item.wbPeriodId] = item.predictors
        })

        return rv
    }

    static PredictorValues_get(
        dct: IPredictorDictionary,
        wbCustId: number,
        wbPrdId: number,
        wbPeriodId: number
    ): IPredictors {
        if (_.isEmpty(dct)) return undefined
        if (!(wbCustId in dct)) return undefined
        const dct2 = dct[wbCustId]
        if (!(wbPrdId in dct2)) return undefined
        const dct3 = dct2[wbPrdId]
        if (!(wbPeriodId in dct3)) return undefined

        return dct3[wbPeriodId]
    }

    private static async makeRequest(aRequest: IModelDataSetServiceRequest): Promise<IModelDataSetServiceResponse> {
        let sEndPoint = <string>(PAEnvironment.getValue(PAEnvironment.Network_URL_Base), '/')
        if (sEndPoint.endsWith('/')) {
            sEndPoint += 'modelDatasetService'
        } else {
            sEndPoint += '/modelDatasetService'
        }

        const resp = await axios.post(sEndPoint, { data: { request: aRequest } })
        const responseData: IModelDataSetServiceResponse = resp.data
        return responseData
    }
}
